import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, AlertCircle, Info, Clock } from 'lucide-react';
import { maintenanceApi } from '@/api/maintenanceApi';
import { format } from 'date-fns';

interface MaintenanceAlertsTabProps {
  farmId: string;
}

const severityConfig = {
  critical: {
    icon: AlertCircle,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200/50',
    textColor: 'text-red-800',
    iconColor: 'text-red-600',
    badgeBg: 'bg-red-100',
    badgeText: 'text-red-700',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200/50',
    textColor: 'text-amber-800',
    iconColor: 'text-amber-600',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200/50',
    textColor: 'text-blue-800',
    iconColor: 'text-blue-600',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
  },
};

const alertTypeLabels: Record<string, string> = {
  equipment: 'Equipment',
  water_level: 'Water Level',
  environmental: 'Environmental',
  discharge: 'Discharge',
};

export const MaintenanceAlertsTab: React.FC<MaintenanceAlertsTabProps> = ({
  farmId,
}) => {
  const { data: alerts = [], isLoading, error } = useQuery({
    queryKey: ['maintenance-alerts', farmId],
    queryFn: () => maintenanceApi.getAlerts(farmId),
    enabled: !!farmId,
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200/50 p-8">
        <div className="text-center text-slate-500">Loading alerts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-slate-200/50 p-8">
        <div className="text-center text-red-500">Failed to load alerts</div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-slate-200/50 p-8">
        <div className="text-center py-12">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 border border-emerald-200/50 mb-4">
            <svg className="h-6 w-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="font-roboto text-sm font-semibold text-gray-900">
            No alerts
          </h3>
          <p className="font-roboto mt-1 text-sm text-gray-600">
            All maintenance checks are looking good. No issues detected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-roboto text-lg font-semibold text-gray-900">
          Active Alerts ({alerts.length})
        </h2>
      </div>

      <div className="space-y-3">
        {alerts.map((alert) => {
          const config = severityConfig[alert.severity] || severityConfig.info;
          const Icon = config.icon;

          return (
            <div
              key={alert.id}
              className={`${config.bgColor} ${config.borderColor} border rounded-lg p-4`}
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 ${config.iconColor}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.badgeBg} ${config.badgeText}`}>
                      {alert.severity.toUpperCase()}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                      {alertTypeLabels[alert.alertType] || alert.alertType}
                    </span>
                    <span className="text-sm text-slate-500">
                      {alert.pondName}
                    </span>
                  </div>
                  <p className={`text-sm font-medium ${config.textColor}`}>
                    {alert.message}
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                    <Clock className="h-3 w-3" />
                    <span>{format(new Date(alert.timestamp), 'PPpp')}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
